/**
 * QPM Time Synchronization Module
 *
 * 하이브리드 시간 동기화 전략:
 * 1. QPM 서버와 오프셋 계산 (3회 측정, 최소 RTT 선택)
 * 2. 5분마다 재동기화
 * 3. Context propagation으로 상대 시간 전파
 */

class TimeSync {
    constructor(options = {}) {
        this.qpmServerUrl = options.qpmServerUrl || 'http://localhost:3005';
        this.syncInterval = options.syncInterval || 5 * 60 * 1000; // 5분
        this.sampleCount = options.sampleCount || 3;

        this.offset = 0; // QPM 서버와의 시간차 (ms)
        this.lastSyncTime = 0;
        this.syncPromise = null;
        this.intervalId = null;
    }

    /**
     * 동기화 시작
     */
    async start() {
        await this.sync();

        // 주기적 재동기화
        this.intervalId = setInterval(() => {
            this.sync().catch(err => {
                console.warn('[QPM TimeSync] Re-sync failed:', err.message);
            });
        }, this.syncInterval);
    }

    /**
     * 동기화 중지
     */
    stop() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }

    /**
     * QPM 서버와 시간 동기화 수행
     * NTP 유사 알고리즘: 3회 측정 후 최소 RTT 샘플 선택
     */
    async sync() {
        // 이미 동기화 진행 중이면 대기
        if (this.syncPromise) {
            return this.syncPromise;
        }

        this.syncPromise = this._performSync();

        try {
            await this.syncPromise;
        } finally {
            this.syncPromise = null;
        }
    }

    async _performSync() {
        const samples = [];

        for (let i = 0; i < this.sampleCount; i++) {
            try {
                const sample = await this._collectSample();
                samples.push(sample);

                // 샘플 간 약간의 딜레이
                if (i < this.sampleCount - 1) {
                    await this._delay(100);
                }
            } catch (err) {
                console.warn(`[QPM TimeSync] Sample ${i + 1} failed:`, err.message);
            }
        }

        if (samples.length === 0) {
            throw new Error('All sync samples failed');
        }

        // 최소 RTT 샘플 선택 (가장 정확한 샘플)
        const bestSample = samples.reduce((best, current) =>
            current.rtt < best.rtt ? current : best
        );

        this.offset = bestSample.offset;
        this.lastSyncTime = Date.now();

    }

    /**
     * 단일 동기화 샘플 수집
     */
    async _collectSample() {
        const t0 = this._now(); // 클라이언트 전송 시간

        const response = await this._fetch(`${this.qpmServerUrl}/api/time/sync`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ clientTime: t0 })
        });

        const t3 = this._now(); // 클라이언트 수신 시간
        const data = await response.json();

        const t1 = data.serverReceiveTime; // 서버 수신 시간
        const t2 = data.serverSendTime;    // 서버 전송 시간

        // RTT (Round Trip Time)
        const rtt = (t3 - t0) - (t2 - t1);

        // Offset 계산 (NTP 알고리즘)
        // offset = ((t1 - t0) + (t2 - t3)) / 2
        const offset = ((t1 - t0) + (t2 - t3)) / 2;

        return { rtt, offset, t0, t1, t2, t3 };
    }

    /**
     * 현재 동기화된 시간 반환
     */
    getSyncedTime() {
        return this._now() + this.offset;
    }

    /**
     * 로컬 시간을 동기화된 시간으로 변환
     */
    toSyncedTime(localTime) {
        return localTime + this.offset;
    }

    /**
     * 동기화된 시간을 로컬 시간으로 변환
     */
    toLocalTime(syncedTime) {
        return syncedTime - this.offset;
    }

    /**
     * 현재 오프셋 반환
     */
    getOffset() {
        return this.offset;
    }

    /**
     * 동기화 상태 확인
     */
    isSynced() {
        return this.lastSyncTime > 0;
    }

    /**
     * 마지막 동기화 이후 경과 시간
     */
    getTimeSinceLastSync() {
        if (!this.lastSyncTime) return Infinity;
        return Date.now() - this.lastSyncTime;
    }

    // Private helpers
    _now() {
        return Date.now();
    }

    _delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    async _fetch(url, options) {
        // 브라우저/Node.js 통합 - 네이티브 fetch 사용
        // Node.js 18+ 또는 브라우저에서 동작
        return fetch(url, options);
    }
}

/**
 * Trace Context for distributed tracing
 * W3C Trace Context 형식 지원
 */
class TraceContext {
    constructor(options = {}) {
        this.serviceCallId = options.serviceCallId || this._generateId(32);
        this.spanId = options.spanId || this._generateId(16);
        this.parentSpanId = options.parentSpanId || null;
        this.sampled = options.sampled !== false;

        // QPM 확장 필드
        this.startTime = options.startTime || Date.now();
        this.syncedStartTime = options.syncedStartTime || null;
        this.offset = options.offset || 0;
    }

    /**
     * W3C traceparent 헤더 생성
     * Format: {version}-{trace-id}-{parent-id}-{trace-flags}
     */
    toTraceparent() {
        const flags = this.sampled ? '01' : '00';
        return `00-${this.serviceCallId}-${this.spanId}-${flags}`;
    }

    /**
     * QPM 전용 헤더 생성 (시간 정보 포함)
     */
    toQPMHeader() {
        return JSON.stringify({
            serviceCallId: this.serviceCallId,
            spanId: this.spanId,
            parentSpanId: this.parentSpanId,
            startTime: this.startTime,
            syncedStartTime: this.syncedStartTime,
            offset: this.offset
        });
    }

    /**
     * traceparent 헤더에서 파싱
     */
    static fromTraceparent(header) {
        if (!header) return null;

        const parts = header.split('-');
        if (parts.length !== 4) return null;

        return new TraceContext({
            serviceCallId: parts[1],
            spanId: TraceContext._generateId(16), // 새 span
            parentSpanId: parts[2],
            sampled: parts[3] === '01'
        });
    }

    /**
     * QPM 헤더에서 파싱
     */
    static fromQPMHeader(header) {
        if (!header) return null;

        try {
            const data = JSON.parse(header);
            return new TraceContext({
                serviceCallId: data.serviceCallId,
                spanId: TraceContext._generateId(16), // 새 span
                parentSpanId: data.spanId,
                sampled: true,
                startTime: data.startTime,
                syncedStartTime: data.syncedStartTime,
                offset: data.offset
            });
        } catch {
            return null;
        }
    }

    /**
     * 자식 context 생성
     */
    createChild() {
        return new TraceContext({
            serviceCallId: this.serviceCallId,
            parentSpanId: this.spanId,
            sampled: this.sampled,
            startTime: Date.now(),
            syncedStartTime: this.syncedStartTime ? Date.now() + this.offset : null,
            offset: this.offset
        });
    }

    _generateId(length) {
        return TraceContext._generateId(length);
    }

    static _generateId(length) {
        const chars = '0123456789abcdef';
        let result = '';
        for (let i = 0; i < length; i++) {
            result += chars[Math.floor(Math.random() * 16)];
        }
        return result;
    }
}

// ESM Export
export { TimeSync, TraceContext };
